import Button from 'components/controls/Button';
import { ConditionalRender } from 'components/layout/ConditionalRender';
import { FormFieldData, useForm } from 'modules/forms';
import { usePermissions, UserPermission, WriteOn } from 'modules/permissions';
import { useCallback, useState } from 'react';
import { ObservationStatusEnum } from 'shared/enums/ObservationStatus.enum';
import { Issue } from '../../../queries/view-issue.queries.types';
import {
   StyledActionsContainer,
   StyledObservationHeader,
   StyledActionsRow,
   StyledActionsCol
} from 'shared/styled/ObservationHeader.styled';
import CompleteDialog from './CompleteDialog';
import { IFormIsValidFunction } from 'modules/forms/types/IFormIsValidFunction';
import { Prompt } from 'react-router-dom';
import { ObservationFileEntity } from 'shared/types/file/ObservationFileEntity';
import { FileStatus } from 'shared/enums/FileStatus.enum';
import { AmmendIssueLoadingState } from '../../../enums/ammend-near-miss-loading-state.enum';
import { ConfirmationDialog } from 'components/layout/Dialog';
import { IAddFiles } from 'shared/utils/files/types/IAddFiles';
import { IUpdateFiles } from 'shared/utils/files/types/IUpdateFiles';
import { IRemoveFiles } from 'shared/utils/files/types/IRemoveFiles';
import { IssueEntity } from 'shared/types/domain/IssueEntity';

interface IssueHeaderProps {
   issue: Issue | undefined;
   onSave: (
      formData: FormFieldData<IssueEntity> | undefined,
      fileData: ObservationFileEntity[] | undefined,
      isValid: IFormIsValidFunction
   ) => void;
   onComplete: () => void;
   onReOpen: () => void;
   setLoadingState: (loadingMessage: AmmendIssueLoadingState, isLoading: boolean) => void;
   isLoading: boolean;
   files: ObservationFileEntity[];
   handleAddFile: IAddFiles<ObservationFileEntity>;
   handleUpdateFiles: IUpdateFiles<ObservationFileEntity>;
   handleDeleteEvidence: IRemoveFiles<ObservationFileEntity>;
}

const IssueHeader = ({
   issue,
   onSave,
   onComplete,
   onReOpen,
   setLoadingState,
   isLoading,
   files,
   handleAddFile,
   handleUpdateFiles,
   handleDeleteEvidence
}: IssueHeaderProps) => {
   const getTitle = (): string => {
      if (issue?.whatHappened) {
         if (issue.whatHappened.length > 100) {
            return `${issue.whatHappened.substring(0, 100)}...`;
         }

         return issue.whatHappened;
      }

      return 'Issue Loading...';
   };

   const [completeFormOpen, setCompleteFormOpen] = useState<boolean>(false);
   const [showReOpenForm, setShowReOpenForm] = useState<boolean>(false);

   const { formData, isValid } = useForm<IssueEntity>();
   const { hasPermission } = usePermissions();

   const hasChanges = useCallback((): boolean => {
      var dataChanged =
         issue?.concernLevel !== formData?.concernLevel ||
         issue?.reachId !== formData?.reachId ||
         issue?.processId !== formData?.processId ||
         issue?.depotPcId !== formData?.depotPcId ||
         issue?.whatHappened !== formData?.whatHappened ||
         issue?.howToStop !== formData?.howToStop ||
         issue?.reporterType !== formData?.reporterType ||
         issue?.anonymousEntry !== formData?.anonymousEntry ||
         issue?.reportedBy?.reporterContactName !== formData?.reporterContactName ||
         issue?.reportedBy?.reporterPhoneNumber !== formData?.reporterPhoneNumber ||
         issue?.reportedBy?.reporterEmail !== formData?.reporterEmail ||
         files?.filter(f => f.fileStatus === FileStatus.Removed && f.type?.id !== 11).length > 0 ||
         issue?.incidentDate !== formData?.incidentDate;
      if (dataChanged) {
         window.onbeforeunload = () => true;
      } else {
         window.onbeforeunload = null;
      }
      return dataChanged;
   }, [issue, formData, files]);

   const canSave = useCallback((): boolean => {
      return (
         isStillOpen(issue) &&
         (hasPermission(WriteOn(UserPermission.ExistingObservation)) ||
            hasPermission(WriteOn(UserPermission.IncidentDate)) ||
            hasPermission(WriteOn(UserPermission.ObservationReporter)))
      );
   }, [formData, issue, hasPermission]);

   const canComplete = useCallback((): boolean => {
      return isStillOpen(issue) && hasPermission(WriteOn(UserPermission.Completion));
   }, [formData, issue, hasPermission]);

   const canReOpen = useCallback((): boolean => {
      return !isStillOpen(issue) && hasPermission(WriteOn(UserPermission.ReOpen));
   }, [formData, issue, hasPermission]);

   const saveIssue = (): void => {
      onSave(
         formData,
         files.filter(x => x.type?.id !== 11),
         isValid
      );
   };

   const completeIssue = (): void => {
      setCompleteFormOpen(false);
   };

   const reOpenIssue = (): void => {
      setShowReOpenForm(false);
      onReOpen();
   };

   const closeAndResetCompletionForm = (): void => {
      setCompleteFormOpen(false);
   };

   const reOpenText = useCallback((): string => {
      if (issue?.observationStatus == ObservationStatusEnum.Archived) return 'Unarchive';
      if (issue?.observationStatus == ObservationStatusEnum.Closed) return 'Reopen';

      return '';
   }, [issue]);

   const reOpenConfirmationText = useCallback((): string => {
      if (issue?.observationStatus == ObservationStatusEnum.Archived)
         return 'Retrieving this record from archive means it will be re-opened, do you want to continue?';
      if (issue?.observationStatus == ObservationStatusEnum.Closed) return "Are you sure you want to reopen this issue?";
      return '';
   }, [issue]);

   return (
      <StyledObservationHeader>
         <Prompt when={hasChanges()} message={'You have unsaved changes to the current near miss, are you sure you wish to navigate away?'} />
         <h1>{getTitle()}</h1>
         <StyledActionsContainer fluid>
            <StyledActionsRow>
               <StyledActionsCol xs={12} style={{ justifyContent: 'flex-end' }}>
                  <ConditionalRender condition={canSave}>
                     <Button
                        isLoading={isLoading}
                        text={'Save Changes'}
                        onClick={saveIssue}
                        iconName='Save'
                        disabled={!(isValid(false) && hasChanges())}
                     />
                  </ConditionalRender>

                  <ConditionalRender condition={canComplete}>
                     <CompleteDialog
                        issue={issue}
                        isOpen={completeFormOpen}
                        setLoadingState={setLoadingState}
                        onDismiss={closeAndResetCompletionForm}
                        onComplete={completeIssue}
                        files={files.filter(x => x.type?.id === 11)}
                        handleAddEvidence={handleAddFile}
                        handleUpdateFiles={handleUpdateFiles}
                        handleDeleteEvidence={handleDeleteEvidence}
                     />

                     <Button
                        style={{ marginLeft: '15px' }}
                        isLoading={isLoading}
                        text={'Complete and Action'}
                        onClick={() => setCompleteFormOpen(true)}
                        iconName='CheckMark'
                        disabled={hasChanges()}
                     />
                  </ConditionalRender>

                  <ConditionalRender condition={canReOpen}>
                     <ConfirmationDialog
                        show={showReOpenForm}
                        hidden={true}
                        title={`${reOpenText()} Issue`}
                        subText={reOpenConfirmationText()}
                        onDecline={() => setShowReOpenForm(false)}
                        onAccept={reOpenIssue}
                        acceptButtonText='Proceed'
                        declineButtonText='Cancel'
                     />

                     <Button
                        style={{ marginLeft: '15px' }}
                        isLoading={isLoading}
                        text={reOpenText()}
                        onClick={() => setShowReOpenForm(true)}
                        iconName='Undo'
                        disabled={hasChanges()}
                     />
                  </ConditionalRender>
               </StyledActionsCol>
            </StyledActionsRow>
         </StyledActionsContainer>
      </StyledObservationHeader>
   );
};

export default IssueHeader;

function isStillOpen(issue: Issue | undefined): boolean {
   return issue?.observationStatus == ObservationStatusEnum.Open;
}
